import React, { useState } from 'react';
import { FiX, FiCopy, FiCheck, FiExternalLink, FiDownload } from 'react-icons/fi';
import { GuestCVUpload } from '../types';

interface GuestCVDetailModalProps {
  cv: GuestCVUpload;
  onClose: () => void;
}

const GuestCVDetailModal: React.FC<GuestCVDetailModalProps> = ({ cv, onClose }) => {
  const [activeTab, setActiveTab] = useState<'text' | 'file' | 'analysis'>('analysis');
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return 'Invalid date';
    }
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const getScoreBadgeColor = (score: number | string | null): string => {
    const numScore = typeof score === 'string' ? parseFloat(score) : score;
    if (!numScore) return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    if (numScore >= 80) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    if (numScore >= 60) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  };

  const formatBytes = (bytes: number | undefined): string => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    if (mb >= 1) return `${mb.toFixed(2)} MB`;
    return `${kb.toFixed(2)} KB`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-lg bg-white shadow-xl dark:bg-navy-800">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-navy-700">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Guest CV Details</h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">ID: #{cv.id}</span>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-navy-700"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[calc(90vh-80px)] overflow-y-auto p-6">
          {/* Status Banner */}
          <div className="mb-6 flex items-center justify-between rounded-lg bg-gradient-to-r from-purple-50 to-purple-100 p-4 dark:from-purple-900/20 dark:to-purple-800/20">
            <div className="flex items-center space-x-4">
              <div>
                <p className="text-sm font-medium text-purple-700 dark:text-purple-300">Overall Score</p>
                <p className={`text-3xl font-bold ${(() => {
                  const score = typeof cv.score === 'string' ? parseFloat(cv.score) : cv.score;
                  return score && score >= 80 ? 'text-green-600' : score && score >= 60 ? 'text-yellow-600' : 'text-red-600';
                })()}`}>
                  {cv.score !== null ? `${typeof cv.score === 'string' ? parseFloat(cv.score).toFixed(0) : cv.score}%` : 'N/A'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-purple-700 dark:text-purple-300">Analyzed</p>
              <p className="text-sm text-purple-900 dark:text-purple-100">{formatDate(cv.analyzed_at)}</p>
            </div>
          </div>

          {/* Guest Session Info */}
          <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-navy-700 dark:bg-navy-700">
            <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">Guest Session Information</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Session ID</p>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {cv.guest_session_info?.id || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Credits Used</p>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {cv.guest_session_info ? `${cv.guest_session_info.used_credits} / ${cv.guest_session_info.total_credits}` : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Fingerprint</p>
                <div className="mt-1 flex items-center space-x-2">
                  <p className="truncate text-sm text-gray-900 dark:text-white">
                    {cv.fingerprint || 'N/A'}
                  </p>
                  {cv.fingerprint && (
                    <button
                      onClick={() => handleCopy(cv.fingerprint!, 'fingerprint')}
                      className="text-gray-400 hover:text-brand-500"
                    >
                      {copiedText === 'fingerprint' ? (
                        <FiCheck className="h-4 w-4 text-green-500" />
                      ) : (
                        <FiCopy className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Extension ID</p>
                <div className="mt-1 flex items-center space-x-2">
                  <p className="truncate text-sm text-gray-900 dark:text-white">
                    {cv.extension_id || 'N/A'}
                  </p>
                  {cv.extension_id && (
                    <button
                      onClick={() => handleCopy(cv.extension_id!, 'extension_id')}
                      className="text-gray-400 hover:text-brand-500"
                    >
                      {copiedText === 'extension_id' ? (
                        <FiCheck className="h-4 w-4 text-green-500" />
                      ) : (
                        <FiCopy className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
              {cv.source_url && (
                <div className="md:col-span-2">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Source URL</p>
                  <a
                    href={cv.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 flex items-center space-x-2 text-sm text-brand-500 hover:text-brand-600"
                  >
                    <span className="truncate">{cv.source_url}</span>
                    <FiExternalLink className="h-4 w-4 flex-shrink-0" />
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
            <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">Candidate Information</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</p>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{cv.candidate_name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</p>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{cv.email || cv.contact_info?.email || 'Not extracted'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</p>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{cv.phone || cv.contact_info?.phone || 'Not extracted'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Age</p>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{cv.age || cv.contact_info?.age || 'Not extracted'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Experience</p>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {(cv.years_of_experience || cv.contact_info?.years_of_experience) ? `${cv.years_of_experience || cv.contact_info?.years_of_experience} years` : 'Not extracted'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</p>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{cv.address || cv.candidate_location || cv.contact_info?.address || 'Not extracted'}</p>
              </div>
            </div>
          </div>

          {/* Job Snapshot */}
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
            <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">Job Criteria Used</h3>
            {cv.job_data_snapshot ? (
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Job Title</p>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{cv.job_data_snapshot.title || 'N/A'}</p>
                </div>
                {cv.job_data_snapshot.description && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</p>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{cv.job_data_snapshot.description}</p>
                  </div>
                )}
                {cv.job_data_snapshot.skills && cv.job_data_snapshot.skills.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Required Skills</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {cv.job_data_snapshot.skills.map((skill: any, index: number) => (
                        <span
                          key={index}
                          className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                        >
                          {skill.name} (Priority: {skill.priority})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Experience Weight</p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{cv.job_data_snapshot.experience_weight || 0}%</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Skills Weight</p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{cv.job_data_snapshot.skills_weight || 0}%</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Education Weight</p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{cv.job_data_snapshot.education_weight || 0}%</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Language Weight</p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{cv.job_data_snapshot.language_weight || 0}%</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No job data snapshot available</p>
            )}
          </div>

          {/* Tabs */}
          <div className="mb-4 border-b border-gray-200 dark:border-navy-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('analysis')}
                className={`border-b-2 py-4 px-1 text-sm font-medium ${
                  activeTab === 'analysis'
                    ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Analysis Results
              </button>
              <button
                onClick={() => setActiveTab('text')}
                className={`border-b-2 py-4 px-1 text-sm font-medium ${
                  activeTab === 'text'
                    ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                CV Text
              </button>
              <button
                onClick={() => setActiveTab('file')}
                className={`border-b-2 py-4 px-1 text-sm font-medium ${
                  activeTab === 'file'
                    ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                File Info
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
            {activeTab === 'analysis' && (
              <div className="space-y-6">
                {/* Detailed Scores - Horizontal Bars */}
                <div>
                  <h4 className="mb-4 font-semibold text-gray-900 dark:text-white">Analysis Scores</h4>
                  <div className="flex flex-col gap-3">
                    {cv.experience_score !== undefined && cv.experience_score !== null && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-24">Experience</span>
                        <div className="w-[200px] h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500 dark:bg-green-400 rounded-full transition-all"
                            style={{ width: `${typeof cv.experience_score === 'string' ? parseFloat(cv.experience_score) : cv.experience_score}%` }}
                          />
                        </div>
                        <span className="text-sm font-bold text-gray-700 dark:text-gray-300 w-10 text-right">
                          {Math.round(typeof cv.experience_score === 'string' ? parseFloat(cv.experience_score) : cv.experience_score)}
                        </span>
                      </div>
                    )}
                    {cv.skills_score !== undefined && cv.skills_score !== null && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-24">Skills</span>
                        <div className="w-[200px] h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-purple-500 dark:bg-purple-400 rounded-full transition-all"
                            style={{ width: `${typeof cv.skills_score === 'string' ? parseFloat(cv.skills_score) : cv.skills_score}%` }}
                          />
                        </div>
                        <span className="text-sm font-bold text-gray-700 dark:text-gray-300 w-10 text-right">
                          {Math.round(typeof cv.skills_score === 'string' ? parseFloat(cv.skills_score) : cv.skills_score)}
                        </span>
                      </div>
                    )}
                    {cv.education_score !== undefined && cv.education_score !== null && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-24">Education</span>
                        <div className="w-[200px] h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-orange-500 dark:bg-orange-400 rounded-full transition-all"
                            style={{ width: `${typeof cv.education_score === 'string' ? parseFloat(cv.education_score) : cv.education_score}%` }}
                          />
                        </div>
                        <span className="text-sm font-bold text-gray-700 dark:text-gray-300 w-10 text-right">
                          {Math.round(typeof cv.education_score === 'string' ? parseFloat(cv.education_score) : cv.education_score)}
                        </span>
                      </div>
                    )}
                    {cv.language_score !== undefined && cv.language_score !== null && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-24">Language</span>
                        <div className="w-[200px] h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 dark:bg-blue-400 rounded-full transition-all"
                            style={{ width: `${typeof cv.language_score === 'string' ? parseFloat(cv.language_score) : cv.language_score}%` }}
                          />
                        </div>
                        <span className="text-sm font-bold text-gray-700 dark:text-gray-300 w-10 text-right">
                          {Math.round(typeof cv.language_score === 'string' ? parseFloat(cv.language_score) : cv.language_score)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Reasoning Section */}
                {cv.reasoning && Object.keys(cv.reasoning).length > 0 && (
                  <div>
                    <h4 className="mb-3 font-semibold text-gray-900 dark:text-white">Reasoning</h4>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-navy-700 dark:bg-navy-700">
                      <div className="space-y-3">
                        {cv.reasoning.overall && (
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Overall</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{cv.reasoning.overall}</p>
                          </div>
                        )}
                        {cv.reasoning.experience && (
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Experience</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{cv.reasoning.experience}</p>
                          </div>
                        )}
                        {cv.reasoning.skills && (
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Skills</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{cv.reasoning.skills}</p>
                          </div>
                        )}
                        {cv.reasoning.education && (
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Education</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{cv.reasoning.education}</p>
                          </div>
                        )}
                        {cv.reasoning.language && (
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Language</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{cv.reasoning.language}</p>
                          </div>
                        )}
                        {cv.reasoning.conclusion && (
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Conclusion</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{cv.reasoning.conclusion}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Skills Match */}
                {(cv.matched_skills?.length || cv.missing_skills?.length) && (
                  <div>
                    <h4 className="mb-3 font-semibold text-gray-900 dark:text-white">Skills Match</h4>
                    <div className="space-y-4">
                      {cv.matched_skills && cv.matched_skills.length > 0 && (
                        <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-900/20">
                          <p className="mb-3 text-sm font-bold text-green-700 dark:text-green-300">✓ Matched Skills ({cv.matched_skills.length})</p>
                          <div className="flex flex-wrap gap-2">
                            {cv.matched_skills.map((skill, index) => (
                              <span
                                key={index}
                                className="rounded-full bg-green-100 px-3 py-1.5 text-sm font-medium text-green-700 dark:bg-green-800 dark:text-green-200"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {cv.missing_skills && cv.missing_skills.length > 0 && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-900/20">
                          <p className="mb-3 text-sm font-bold text-red-700 dark:text-red-300">✗ Missing Skills ({cv.missing_skills.length})</p>
                          <div className="flex flex-wrap gap-2">
                            {cv.missing_skills.map((skill, index) => (
                              <span
                                key={index}
                                className="rounded-full bg-red-100 px-3 py-1.5 text-sm font-medium text-red-700 dark:bg-red-800 dark:text-red-200"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Strengths & Weaknesses */}
                {(cv.strengths?.length || cv.weaknesses?.length) && (
                  <div>
                    <h4 className="mb-3 font-semibold text-gray-900 dark:text-white">Analysis Results</h4>
                    <div className="grid gap-4 md:grid-cols-2">
                      {cv.strengths && cv.strengths.length > 0 && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-900/20">
                          <p className="mb-3 text-sm font-bold text-blue-700 dark:text-blue-300">Strengths</p>
                          <ul className="space-y-2">
                            {cv.strengths.map((strength, index) => (
                              <li key={index} className="text-sm text-blue-800 dark:text-blue-200 flex gap-2">
                                <span className="text-blue-500">•</span>
                                <span>{strength}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {cv.weaknesses && cv.weaknesses.length > 0 && (
                        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-900 dark:bg-orange-900/20">
                          <p className="mb-3 text-sm font-bold text-orange-700 dark:text-orange-300">Improvements</p>
                          <ul className="space-y-2">
                            {cv.weaknesses.map((weakness, index) => (
                              <li key={index} className="text-sm text-orange-800 dark:text-orange-200 flex gap-2">
                                <span className="text-orange-500">•</span>
                                <span>{weakness}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Summary */}
                {cv.summary && (
                  <div>
                    <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">Summary</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{cv.summary}</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'text' && (
              <div>
                <h4 className="mb-3 font-semibold text-gray-900 dark:text-white">Extracted CV Text</h4>
                {cv.cv_text && cv.cv_text.trim() ? (
                  <textarea
                    value={cv.cv_text}
                    readOnly
                    rows={15}
                    className="w-full rounded-lg border border-gray-300 bg-gray-50 p-3 font-mono text-sm text-gray-900 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  />
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No CV text available.
                  </p>
                )}
              </div>
            )}

            {activeTab === 'file' && (
              <div>
                <h4 className="mb-3 font-semibold text-gray-900 dark:text-white">File Information</h4>
                {cv.storage_path ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Original Filename</p>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{cv.filename_original}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Stored Filename</p>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{cv.filename_stored || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">File Type</p>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{cv.mime || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">File Size</p>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatBytes(cv.size_bytes)}</p>
                    </div>
                    {cv.download_url && (
                      <div>
                        <a
                          href={cv.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
                        >
                          <FiDownload className="h-4 w-4" />
                          <span>Download CV</span>
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No file uploaded. This CV was analyzed from extracted text only.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestCVDetailModal;
